import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Award, Trophy, TrendingUp, Users, Star } from 'lucide-react';

const AFCA_IMG = 'https://customer-assets.emergentagent.com/job_669fbeff-f080-4dde-bc05-7b6a5c8193e8/artifacts/e6nzyx3n_Braxton__Weldon_FB_png_0PLnr6iK.jpeg';

const TIMELINE = [
  {
    role: 'Offensive Coordinator / Recruiting Coordinator',
    org: 'Sussex County Community College',
    location: 'Newton, NJ',
    years: '2025',
    details: ['Offensive system installation and game planning', 'Recruiting coordination and prospect evaluation'],
  },
  {
    role: 'Head Coach / Offensive Coordinator',
    org: 'Itasca Community College',
    location: 'Grand Rapids, MN',
    years: '2017 – 2020',
    featured: true,
    details: [
      'MCAC Conference Championship — First in school history',
      'First bowl game in school history',
      '#1 ranked offense in MCAC (2018, 2019)',
      '#2 NJCAA national ranking',
      'MCAC Coach of the Year',
      'School record total offense',
      'School record points per game',
      '8 JUCO All-Americans coached',
      'Top 10 NJCAA offense nationally',
      'Built program from non-scholarship foundation to national contender',
    ],
  },
  {
    role: 'Offensive Coordinator',
    org: 'Fivay High School',
    location: 'Hudson, FL',
    years: '2022 – 2023',
    details: ['Offensive system design and implementation', 'Player development and game planning'],
  },
  {
    role: 'Graduate Assistant / Offensive Staff',
    org: 'Arkansas Tech University',
    location: 'Russellville, AR',
    years: '2013 – 2016',
    details: ['D2 football operations and game day preparation', 'Founded the Golden Eagles flag football program from scratch'],
  },
];

const AWARDS = [
  { icon: Award, title: 'AFCA 35 Under 35 Leadership Institute', sub: 'Selected 2021 · Inducted 2022', desc: 'Nationally recognized by the American Football Coaches Association.' },
  { icon: Trophy, title: 'MCAC Conference Championship', sub: '2018', desc: 'First conference championship in Itasca Community College history.' },
  { icon: Star, title: 'MCAC Coach of the Year', sub: '2018', desc: 'Recognized as the top coach in the Minnesota College Athletic Conference.' },
  { icon: TrendingUp, title: '#2 NJCAA National Ranking', sub: '2018', desc: 'Program ranked second nationally among all NJCAA programs.' },
  { icon: Users, title: '8 JUCO All-Americans', sub: '2017 – 2020', desc: 'Developed 8 JUCO All-American selections across multiple seasons.' },
];

export default function Experience() {
  return (
    <div className="bg-gray-950 text-white pt-20">
      <section className="py-16 px-6 border-b border-gray-800">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Coaching Experience</h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">A career built on program development, offensive production, championship results, and player advancement.</p>
        </div>
      </section>

      {/* Awards */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-2xl font-bold mb-8">Awards &amp; Recognition</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {AWARDS.map((a) => (
              <div key={a.title} className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                <a.icon className="h-7 w-7 text-blue-500 mb-3" />
                <h3 className="font-bold mb-1">{a.title}</h3>
                <p className="text-blue-400 text-xs font-semibold mb-2">{a.sub}</p>
                <p className="text-gray-400 text-sm">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AFCA Feature */}
      <section className="py-12 px-6 bg-gray-900 border-y border-gray-800">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <img src={AFCA_IMG} alt="AFCA 35 Under 35" className="rounded-xl w-full shadow-lg" />
            <div className="md:col-span-2">
              <span className="inline-block px-3 py-1 bg-blue-600/20 text-blue-400 text-xs font-bold rounded-full mb-3">National Recognition</span>
              <h2 className="text-2xl font-bold mb-3">AFCA 35 Under 35 Leadership Institute</h2>
              <p className="text-gray-400 text-sm leading-relaxed">Selected in 2021 and inducted in 2022, Coach Braxton was recognized by the American Football Coaches Association as one of the top 35 football coaches under the age of 35 in the country. This honor reflects sustained excellence in leadership, program impact, and contribution to the coaching profession at the national level.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold mb-10">Coaching Timeline</h2>
          <div className="space-y-6">
            {TIMELINE.map((t) => (
              <div key={t.org} className={`rounded-2xl p-8 border ${t.featured ? 'bg-gradient-to-r from-blue-950/40 to-gray-900 border-blue-700/40' : 'bg-gray-900 border-gray-800'}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{t.role}</h3>
                    <p className="text-blue-400 font-semibold">{t.org}</p>
                    <p className="text-gray-500 text-sm">{t.location}</p>
                  </div>
                  <span className={`text-sm font-semibold mt-2 md:mt-0 px-4 py-1.5 rounded-full ${t.featured ? 'bg-blue-600/20 text-blue-400' : 'bg-gray-800 text-gray-400'}`}>{t.years}</span>
                </div>
                <ul className="space-y-2">
                  {t.details.map((d) => (
                    <li key={d} className="flex items-start space-x-2 text-sm text-gray-300">
                      <ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="py-16 px-6 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold mb-8">Education</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-6">
              <p className="text-xs text-blue-400 font-semibold uppercase tracking-wide mb-1">Master&apos;s Degree</p>
              <h3 className="font-bold text-lg mb-1">M.S. Sport Administration</h3>
              <p className="text-gray-400 text-sm">Liberty University</p>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-6">
              <p className="text-xs text-blue-400 font-semibold uppercase tracking-wide mb-1">Bachelor&apos;s Degree</p>
              <h3 className="font-bold text-lg mb-1">B.S. Recreation &amp; Park Administration</h3>
              <p className="text-gray-400 text-sm">Arkansas Tech University</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold mb-10">Interested in Connecting?</h2>
          <Link to="/contact" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full transition-all inline-flex items-center">
            Get in Touch <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
